import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  SubscriptionPlan,
  UserSubscription,
 
} from "../../types/subscription";
import { ApiService } from "../../services/apiService";
import subscriptionService from "../../services/subscriptionService";

export type PlanForLLM = { id: string; name: string };

interface SubscriptionState {
  plans: SubscriptionPlan[];
  plansForLLM: PlanForLLM[];
  userSubscriptions: UserSubscription[];
  selectedPlan: SubscriptionPlan | null;
  isLoading: boolean;
  plansForLLMLoading: boolean;
  error: string | null;
  plansForLLMError: string | null;
}

const initialState: SubscriptionState = {
  plans: [],
  plansForLLM: [],
  userSubscriptions: [],
  selectedPlan: null,
  isLoading: false,
  plansForLLMLoading: false,
  error: null,
  plansForLLMError: null,
};

export const fetchSubscriptionPlans = createAsyncThunk(
  "subscription/fetchPlans",
  async (_, { rejectWithValue }) => {
    try {
      const plans = await ApiService.get<any>("/get-subscription-plans");
      return plans?.data || [];
    } catch (error) {
      return rejectWithValue(
        (error as any).message || "Failed to fetch subscription plans"
      );
    }
  }
);

export const fetchSubscriptionPlansForLLM = createAsyncThunk<
  PlanForLLM[],
  void,
  { rejectValue: string }
>("subscription/fetchPlansForLLM", async (_, { rejectWithValue }) => {
  try {
    const plans = await subscriptionService.getSubscriptionPlansForLLM();
    return plans;
  } catch (error) {
    return rejectWithValue(
      (error as Error)?.message || "Failed to fetch subscription plans for LLM"
    );
  }
});

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    setSelectedPlan: (
      state,
      action: PayloadAction<SubscriptionPlan | null>
    ) => {
      state.selectedPlan = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptionPlans.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
        state.isLoading = false;
        state.plans = action.payload;
      })
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as string) || "Failed to fetch subscription plans";
      })
      .addCase(fetchSubscriptionPlansForLLM.pending, (state) => {
        state.plansForLLMLoading = true;
        state.plansForLLMError = null;
      })
      .addCase(fetchSubscriptionPlansForLLM.fulfilled, (state, action) => {
        state.plansForLLMLoading = false;
        state.plansForLLM = action.payload;
      })
      .addCase(fetchSubscriptionPlansForLLM.rejected, (state, action) => {
        state.plansForLLMLoading = false;
        state.plansForLLMError =
          action.payload || "Failed to fetch subscription plans for LLM";
      });
  },
});

export const {
  setSelectedPlan,
  clearError,
  setLoading,
} = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
